import Grid from '@mui/material/Grid2';
import * as React from 'react';
import { observer } from 'mobx-react';
import BaseButton from '../../../../components/BaseButton';
import BaseInput from '../../../../components/BaseInput';
import DataStore from '../../../../store';
import { validateForm } from '../../../../helpers/formValidator';
import { notificationStore } from '../../../../store/Notification';
import { MISSING_FIELDS_MESSAGE } from '../../../../helpers/constants';
import socketStore from '../../../../store/Socket';
import ProgressBar from '../../../../components/ProgressBar';
import {
  LAUNCH_BUTTON,
  SOCKET_USER_UPDATE_EVENT,
} from '../../../../constants/base';
import { validationRules } from '../../../../helpers/validationRules';
import BaseSelect from '../../../../components/BaseSelect';
import BaseRadioButtons from '../../../../components/BaseRadioButtons';
import { getPageConfiguration } from '../../../../helpers/groups';
import { useSocketListener } from '../../../../hooks/useSocketListener';
import { ProgressPayload } from '../../../../types/global';

const JobsOverview = observer(() => {
  const [progress, setProgress] = React.useState(0);
  const [progressMax, setProgressMax] = React.useState(0);
  const [progressMessage, setProgressMessage] = React.useState('');

  React.useEffect(() => {
    showOutput();
  }, []);

  const socket = socketStore.socket;
  const channel = React.useMemo(() => SOCKET_USER_UPDATE_EVENT(), []);
  const onProgress = React.useCallback((data: ProgressPayload): void => {
    setProgress(data.progress ?? 0);
    setProgressMax(data.total ?? 0);
    setProgressMessage(data.message ?? '');
  }, []);
  useSocketListener<ProgressPayload>(socket, channel, onProgress, true);

  function showOutput(resData: any = {}): void {
    DataStore.showOutput('Jobs overview launch', resData, 'html');
  }

  const fieldLabels: { [key: string]: string } = {
    maxJobs: 'Max Jobs',
    slideNumber: 'Slide Number',
    wells: 'Wells',
    jobName: 'Job Name',
    jobState: 'Job State',
    lastDay: 'Last Day',
    last3Days: 'Last 3 Days',
    last7Days: 'Last 7 Days',
  };

  const validationSchema = {
    ...{
      slideNumber: validationRules.slideNumber,
      wells: validationRules.wells,
    },
    maxJobs: { type: 'number', min: 1, max: 1000 },
  };
  const [errors, setErrors] = React.useState({});

  const [data, setData] = React.useState({
    maxJobs: '100',
    slideNumber: '',
    wells: '',
    jobName: 'all',
    jobState: 'all',
    filterByDate: '',
  });
  const onChangeData = (event: any) => {
    const { name, value }: any = event.target;
    setData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const launchRun = async (e: any) => {
    e.preventDefault();
    setProgress(0);
    setProgressMax(100);
    setProgressMessage('Starting...');

    const validError = validateForm(data, validationSchema);
    if (!validError) {
      setErrors({});
      const resData: any = await DataStore.jobsOverviewLaunchRequest(data);
      if (resData) {
        showOutput(resData);
      }
    } else {
      const isRequiredMissing = (Object.values(validError) as string[]).some(
        (msg) => msg.toLowerCase().includes('is required')
      );
      const humanizedErrors = Object.entries(validError).reduce(
        (acc: any, [key, message]) => {
          const label = fieldLabels[key] || key;
          const safeMessage =
            typeof message === 'string' ? message : String(message);
          acc[label] = safeMessage.replace(new RegExp(key, 'gi'), label);
          return acc;
        },
        {}
      );
      notificationStore.showMessage(
        isRequiredMissing ? MISSING_FIELDS_MESSAGE : 'Some fields are invalid.',
        'error',
        humanizedErrors
      );
      setErrors(humanizedErrors);
    }
  };

  const getJobStateValues = () => {
    const pageConfiguration: any = getPageConfiguration('jobsOverview');

    return pageConfiguration?.jobState || [];
  };

  const getJobNameValues = () => {
    const pageConfiguration: any = getPageConfiguration('jobsOverview');

    return pageConfiguration?.jobName || [];
  };

  const getFilterByDateItems = () => {
    const pageConfiguration: any = getPageConfiguration('jobsOverview');

    const filterByDateItems = pageConfiguration?.filterByDate || [];

    if (filterByDateItems.length > 0) {
      return filterByDateItems.map((item: string) => ({
        label: fieldLabels[item] || item,
        value: item,
      }));
    }

    return [];
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 2 }}>
          <BaseInput
            feature={'jobsOverview'}
            label={fieldLabels.maxJobs}
            value={data.maxJobs}
            name={'maxJobs'}
            onChange={onChangeData}
            errors={errors}
            required={!!validationSchema?.maxJobs}
            type='number'
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <BaseInput
            feature={'jobsOverview'}
            label={fieldLabels.slideNumber}
            value={data.slideNumber}
            name={'slideNumber'}
            onChange={onChangeData}
            errors={errors}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <BaseInput
            feature={'jobsOverview'}
            label={fieldLabels.wells}
            value={data.wells}
            name={'wells'}
            onChange={onChangeData}
            errors={errors}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <BaseSelect
            feature={'jobsOverview'}
            label={fieldLabels.jobName}
            values={getJobNameValues()}
            value={data.jobName}
            name={'jobName'}
            onChange={onChangeData}
            errors={errors}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <BaseSelect
            feature={'jobsOverview'}
            label={fieldLabels.jobState}
            values={getJobStateValues()}
            value={data.jobState}
            name={'jobState'}
            onChange={onChangeData}
            errors={errors}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 12 }}>
          <BaseRadioButtons
            feature={'jobsOverview'}
            items={getFilterByDateItems()}
            label={'Filter by Date'}
            value={data.filterByDate}
            name={'filterByDate'}
            onChange={onChangeData}
            required={false}
            errors={errors}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 10 }}>
          <BaseButton
            text={LAUNCH_BUTTON}
            variant='contained'
            color='primary'
            isDisabled={DataStore.isProcessing}
            spinner={DataStore.isProcessing}
            onClick={launchRun}
          />
        </Grid>
      </Grid>
      <ProgressBar
        actual={progress}
        max={progressMax}
        message={progressMessage}
      />
    </>
  );
});

export default JobsOverview;
